<template>
  <v-main>
    <v-container fluid class="pa-4">

      <!-- Header -->
      <div class="api-logs-header mb-4">
        <div class="header-content">
          <div class="d-flex align-center">
            <div class="header-icon-wrap mr-3">
              <v-icon color="white" size="26">mdi-api</v-icon>
            </div>
            <div>
              <h2 class="white--text font-weight-bold" style="font-size:22px; letter-spacing:0.3px;">API Logs</h2>
              <span class="white--text" style="font-size:12px; opacity:0.75;">Real-time request monitoring &amp; performance tracking</span>
            </div>
          </div>
          <div class="d-flex align-center" style="gap:10px;">
            <v-chip dark outlined class="white--text" style="border-color:rgba(255,255,255,0.4);">
              <v-icon left small>mdi-database</v-icon>{{ count }} records
            </v-chip>
            <v-btn
              color="white" class="primary--text font-weight-bold"
              elevation="2" rounded @click="statsDialog = true"
            >
              <v-icon left color="primary">mdi-chart-areaspline</v-icon>Statistics
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Filter Card -->
      <v-card elevation="3" rounded="xl" class="mb-4">
        <v-card-text class="pb-2">
          <v-row dense align="center">
            <v-col md="4" sm="12">
              <v-text-field
                v-model="filter.search_keyword"
                label="Search URL, request ID, controller..."
                prepend-inner-icon="mdi-magnify"
                outlined dense rounded clearable hide-details
                background-color="grey lighten-5"
                @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                @keydown.enter="(page = 1), getData()"
              ></v-text-field>
            </v-col>
            <v-col md="2" sm="6">
              <v-autocomplete
                v-model="filter.method" :items="methodOptions"
                label="Method" outlined dense rounded clearable hide-details
                prepend-inner-icon="mdi-code-tags"
                background-color="grey lighten-5"
                @change="(page = 1), getData()"
              ></v-autocomplete>
            </v-col>
            <v-col md="2" sm="6">
              <v-autocomplete
                v-model="filter.status_group" :items="statusGroupOptions"
                label="Status" outlined dense rounded clearable hide-details
                prepend-inner-icon="mdi-signal"
                background-color="grey lighten-5"
                @change="(page = 1), getData()"
              ></v-autocomplete>
            </v-col>
            <v-col md="2" sm="6">
              <v-text-field
                v-model="filter.from_date" label="From"
                type="date" outlined dense rounded clearable hide-details
                background-color="grey lighten-5"
                @change="(page = 1), getData()"
              ></v-text-field>
            </v-col>
            <v-col md="2" sm="6">
              <v-text-field
                v-model="filter.to_date" label="To"
                type="date" outlined dense rounded clearable hide-details
                background-color="grey lighten-5"
                @change="(page = 1), getData()"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Toggle Filters -->
          <div class="d-flex align-center mt-3 mb-1" style="gap:10px;">
            <div
              class="toggle-pill"
              :class="filter.errors_only ? 'toggle-pill--active-red' : ''"
              @click="filter.errors_only = !filter.errors_only; page = 1; getData()"
            >
              <v-icon small :color="filter.errors_only ? 'white' : 'grey darken-1'" class="mr-1">mdi-alert-circle-outline</v-icon>
              <span>Errors Only</span>
            </div>
            <div
              class="toggle-pill"
              :class="filter.slow_only ? 'toggle-pill--active-orange' : ''"
              @click="filter.slow_only = !filter.slow_only; page = 1; getData()"
            >
              <v-icon small :color="filter.slow_only ? 'white' : 'grey darken-1'" class="mr-1">mdi-timer-sand</v-icon>
              <span>Slow &gt; 2s</span>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Table Card -->
      <v-card elevation="3" rounded="xl">
        <!-- Loading bar -->
        <v-progress-linear v-if="isLoading" indeterminate color="primary" height="3" rounded></v-progress-linear>

        <v-card-text class="pa-0">
          <v-simple-table class="api-table">
            <thead>
              <tr class="table-header-row">
                <th class="pl-5">#</th>
                <th>Request ID</th>
                <th>Method</th>
                <th>URL / Controller</th>
                <th>Status</th>
                <th>Duration</th>
                <th>DB Queries</th>
                <th>User</th>
                <th>Time</th>
                <th class="text-center">View</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!isLoading && logs.length === 0">
                <td colspan="10" class="text-center py-10">
                  <v-icon size="48" color="grey lighten-2">mdi-api-off</v-icon>
                  <div class="grey--text mt-2 text-body-2">No API logs found</div>
                </td>
              </tr>
              <tr
                v-else
                v-for="(log, idx) in logs" :key="log.id"
                class="api-row"
                :class="log.is_error ? 'api-row--error' : 'api-row--ok'"
              >
                <!-- Row accent line via class -->
                <td class="pl-5 text-caption grey--text" style="width:40px;">
                  {{ (page - 1) * (rowsPerPage === 'All' ? 0 : rowsPerPage) + idx + 1 }}
                </td>
                <td>
                  <span class="request-id-chip">{{ log.request_id ? log.request_id.substring(0, 8) : '-' }}</span>
                </td>
                <td>
                  <span class="method-badge" :class="'method-' + (log.method || 'get').toLowerCase()">
                    {{ log.method }}
                  </span>
                </td>
                <td style="max-width:320px;">
                  <div class="url-text" :title="log.url">{{ urlPath(log.url) }}</div>
                  <div v-if="log.controller_action" class="controller-text">{{ shortController(log.controller_action) }}</div>
                </td>
                <td>
                  <span class="status-badge" :class="statusBadgeClass(log.response_status)">
                    {{ log.response_status }}
                  </span>
                </td>
                <td>
                  <div class="d-flex align-center" style="gap:6px;">
                    <span :class="log.duration_ms > 2000 ? 'slow-text' : log.duration_ms > 500 ? 'medium-text' : 'fast-text'">
                      {{ log.duration_ms }}ms
                    </span>
                    <div class="duration-bar-wrap">
                      <div class="duration-bar" :class="log.duration_ms > 2000 ? 'duration-bar--slow' : log.duration_ms > 500 ? 'duration-bar--medium' : 'duration-bar--fast'"
                        :style="{ width: Math.min(100, (log.duration_ms / 30)) + 'px' }"></div>
                    </div>
                  </div>
                </td>
                <td>
                  <span :class="log.db_query_count > 50 ? 'query-badge query-badge--critical' : log.db_query_count > 20 ? 'query-badge query-badge--warn' : 'query-count-normal'">
                    <v-icon v-if="log.db_query_count > 20" x-small :color="log.db_query_count > 50 ? 'red' : 'orange'" class="mr-1">mdi-database-alert</v-icon>
                    {{ log.db_query_count || 0 }}
                  </span>
                </td>
                <td>
                  <span v-if="log.user_id" class="user-badge">
                    <v-icon x-small color="grey darken-1" class="mr-1">mdi-account</v-icon>{{ log.user_id }}
                  </span>
                  <span v-else class="grey--text text-caption">—</span>
                </td>
                <td class="text-caption grey--text text--darken-1" style="white-space:nowrap;">{{ formatDate(log.created_at) }}</td>
                <td class="text-center">
                  <v-btn icon small color="primary" :to="'/api-logs/' + log.id" class="view-btn">
                    <v-icon small>mdi-arrow-right-circle-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>

        <!-- Pagination -->
        <v-divider></v-divider>
        <div class="d-flex align-center px-4 py-3" style="position: relative;">
          <div class="d-flex align-center" style="gap: 8px; position: absolute; left: 16px;">
            <span class="text-caption grey--text">Rows per page:</span>
            <v-select
              v-model="rowsPerPage" :items="rowsPerPageOptions" dense outlined hide-details
              style="max-width: 90px;" @change="(page = 1), getData()"
            ></v-select>
          </div>
          <v-pagination
            v-if="rowsPerPage !== 'All'" rounded v-model="page"
            :length="paginationLength" :total-visible="10" class="mx-auto"
          ></v-pagination>
        </div>
      </v-card>

      <!-- Statistics Dialog -->
      <v-dialog v-model="statsDialog" max-width="860" scrollable>
        <v-card rounded="xl" overflow-hidden>
          <div class="stats-dialog-header">
            <div class="d-flex align-center">
              <v-icon color="white" class="mr-2">mdi-chart-areaspline</v-icon>
              <span class="white--text font-weight-bold" style="font-size:16px;">API Statistics — Last 24 Hours</span>
            </div>
            <v-btn icon dark @click="statsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>

          <v-card-text class="pa-5" v-if="stats">
            <!-- Stat Cards -->
            <v-row>
              <v-col md="3" sm="6">
                <div class="stat-card stat-card--blue">
                  <div class="stat-card__icon"><v-icon color="white">mdi-swap-horizontal</v-icon></div>
                  <div class="stat-card__value">{{ stats.total_requests }}</div>
                  <div class="stat-card__label">Total Requests</div>
                </div>
              </v-col>
              <v-col md="3" sm="6">
                <div class="stat-card stat-card--red">
                  <div class="stat-card__icon"><v-icon color="white">mdi-alert-circle</v-icon></div>
                  <div class="stat-card__value">{{ stats.error_requests }}</div>
                  <div class="stat-card__label">Errors</div>
                </div>
              </v-col>
              <v-col md="3" sm="6">
                <div class="stat-card stat-card--green">
                  <div class="stat-card__icon"><v-icon color="white">mdi-speedometer</v-icon></div>
                  <div class="stat-card__value">{{ stats.avg_duration_ms }}ms</div>
                  <div class="stat-card__label">Avg Duration</div>
                </div>
              </v-col>
              <v-col md="3" sm="6">
                <div class="stat-card stat-card--orange">
                  <div class="stat-card__icon"><v-icon color="white">mdi-timer-sand</v-icon></div>
                  <div class="stat-card__value">{{ stats.slow_requests }}</div>
                  <div class="stat-card__label">Slow (&gt;2s)</div>
                </div>
              </v-col>
            </v-row>

            <!-- Top Slow Endpoints -->
            <div class="mt-5">
              <div class="section-title mb-3">
                <v-icon color="orange" class="mr-1" small>mdi-fire</v-icon>Top Slow Endpoints
              </div>
              <v-simple-table dense class="rounded-lg overflow-hidden">
                <thead>
                  <tr class="table-header-row">
                    <th>#</th><th>URL</th><th>Method</th><th>Avg Duration</th><th>Hits</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(ep, i) in stats.top_slow_endpoints" :key="i" class="api-row">
                    <td class="grey--text text-caption">{{ i + 1 }}</td>
                    <td class="text-caption font-weight-medium">{{ urlPath(ep.url) }}</td>
                    <td><span class="method-badge" :class="'method-' + (ep.method || 'get').toLowerCase()">{{ ep.method }}</span></td>
                    <td><span class="slow-text font-weight-bold">{{ Math.round(ep.avg_duration) }}ms</span></td>
                    <td class="text-caption">{{ ep.hit_count }}</td>
                  </tr>
                  <tr v-if="!stats.top_slow_endpoints || stats.top_slow_endpoints.length === 0">
                    <td colspan="5" class="text-center grey--text py-3 text-caption">No data</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>

            <!-- Status Breakdown -->
            <div class="mt-5">
              <div class="section-title mb-3">
                <v-icon color="blue" class="mr-1" small>mdi-chart-donut</v-icon>Status Breakdown
              </div>
              <div class="d-flex flex-wrap" style="gap:10px;">
                <div
                  v-for="(sb, i) in stats.status_breakdown" :key="i"
                  class="status-breakdown-chip"
                  :class="statusBadgeClass(sb.response_status)"
                >
                  <span class="font-weight-bold" style="font-size:15px;">{{ sb.response_status }}</span>
                  <span class="ml-2" style="font-size:12px; opacity:0.85;">{{ sb.count }} reqs</span>
                </div>
              </div>
            </div>
          </v-card-text>

          <v-card-text v-else class="text-center py-10">
            <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
            <div class="grey--text text-caption mt-2">Loading statistics...</div>
          </v-card-text>
        </v-card>
      </v-dialog>

    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "ApiLogsPage",
  data() {
    return {
      logs: [],
      count: 0,
      page: 1,
      rowsPerPage: 20,
      rowsPerPageOptions: [10, 20, 50, 100, "All"],
      paginationLength: 0,
      isLoading: false,
      statsDialog: false,
      stats: null,
      filter: {
        search_keyword: null,
        method: null,
        status_group: null,
        from_date: null,
        to_date: null,
        errors_only: false,
        slow_only: false,
      },
      methodOptions: ["GET", "POST", "PATCH", "PUT", "DELETE"],
      statusGroupOptions: ["2xx", "3xx", "4xx", "5xx"],
    };
  },
  mounted() {
    this.getData();
  },
  watch: {
    page() { this.getData(); },
    statsDialog(val) { if (val && !this.stats) this.getStats(); },
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const isAll = this.rowsPerPage === "All";
        let params = [];
        if (!isAll) params.push(`page=${this.page}&rowsPerPage=${this.rowsPerPage}`);
        if (this.filter.search_keyword) params.push(`search_keyword=${encodeURIComponent(this.filter.search_keyword)}`);
        if (this.filter.method) params.push(`method=${this.filter.method}`);
        if (this.filter.status_group) params.push(`status_group=${this.filter.status_group}`);
        if (this.filter.from_date) params.push(`from_date=${this.filter.from_date}`);
        if (this.filter.to_date) params.push(`to_date=${this.filter.to_date}`);
        if (this.filter.errors_only) params.push(`errors_only=true`);
        if (this.filter.slow_only) params.push(`slow_only=true`);

        const { data } = await axios.get(`/api_logs?${params.join("&")}`);
        this.logs = data.data.data || data.data;
        this.count = data.count;
        if (!isAll) this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    async getStats() {
      try {
        const { data } = await axios.get("/api_logs/statistics");
        this.stats = data.data;
      } catch (e) {
        console.error(e);
      }
    },
    statusBadgeClass(status) {
      if (status >= 500) return "status-5xx";
      if (status >= 400) return "status-4xx";
      if (status >= 300) return "status-3xx";
      return "status-2xx";
    },
    urlPath(url) {
      try { return new URL(url).pathname; } catch { return url; }
    },
    shortController(action) {
      if (!action) return "";
      const parts = action.split("\\");
      return parts[parts.length - 1] || action;
    },
    formatDate(dt) {
      if (!dt) return "-";
      return new Date(dt).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
    },
  },
};
</script>

<style scoped>
/* ===== Header ===== */
.api-logs-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 40%, #1565c0 100%);
  border-radius: 16px;
  padding: 20px 24px;
}
.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.header-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255,255,255,0.18);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ===== Toggle Pills ===== */
.toggle-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1.5px solid #d0d0d0;
  cursor: pointer;
  font-size: 13px;
  color: #555;
  background: #f7f7f7;
  transition: all 0.2s;
  user-select: none;
}
.toggle-pill:hover { background: #ebebeb; }
.toggle-pill--active-red {
  background: #c62828;
  border-color: #c62828;
  color: white;
}
.toggle-pill--active-orange {
  background: #e65100;
  border-color: #e65100;
  color: white;
}

/* ===== Table ===== */
.api-table thead tr th {
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #666 !important;
  background: #fafafa !important;
  border-bottom: 2px solid #eeeeee !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}
.api-row {
  border-left: 3px solid transparent;
  transition: background 0.15s;
}
.api-row:hover { background: #f5f7ff !important; }
.api-row--error { border-left-color: #ef5350 !important; background: #fff8f8; }
.api-row--ok { border-left-color: transparent; }
.api-row td { padding: 10px 14px !important; vertical-align: middle; }

/* ===== Badges ===== */
.method-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
}
.method-get    { background: #e8f5e9; color: #2e7d32; }
.method-post   { background: #e3f2fd; color: #1565c0; }
.method-patch  { background: #fff3e0; color: #e65100; }
.method-put    { background: #fff8e1; color: #f57f17; }
.method-delete { background: #fce4ec; color: #c62828; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
}
.status-2xx { background: #e8f5e9; color: #2e7d32; }
.status-3xx { background: #e3f2fd; color: #1565c0; }
.status-4xx { background: #fff3e0; color: #e65100; }
.status-5xx { background: #fce4ec; color: #c62828; }

.request-id-chip {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  background: #f0f0f0;
  color: #555;
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.5px;
}

/* ===== Duration ===== */
.fast-text   { color: #2e7d32; font-size: 13px; font-weight: 600; }
.medium-text { color: #f57c00; font-size: 13px; font-weight: 600; }
.slow-text   { color: #c62828; font-size: 13px; font-weight: 700; }
.duration-bar-wrap {
  height: 4px;
  background: #f0f0f0;
  border-radius: 4px;
  width: 60px;
  overflow: hidden;
}
.duration-bar {
  height: 4px;
  border-radius: 4px;
  min-width: 3px;
}
.duration-bar--fast   { background: #43a047; }
.duration-bar--medium { background: #fb8c00; }
.duration-bar--slow   { background: #e53935; }

/* ===== DB Queries ===== */
.query-count-normal { font-size: 13px; color: #444; }
.query-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
}
.query-badge--warn     { background: #fff3e0; color: #e65100; }
.query-badge--critical { background: #fce4ec; color: #c62828; }

/* ===== URL + Controller ===== */
.url-text {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 310px;
}
.controller-text {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 310px;
}

/* ===== User ===== */
.user-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: #444;
  background: #f3f3f3;
  padding: 2px 8px;
  border-radius: 6px;
}

/* ===== View Button ===== */
.view-btn { opacity: 0.6; transition: opacity 0.15s; }
.api-row:hover .view-btn { opacity: 1; }

/* ===== Statistics Dialog ===== */
.stats-dialog-header {
  background: linear-gradient(135deg, #1a237e, #1565c0);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 12px 12px 0 0;
}
.stat-card {
  border-radius: 12px;
  padding: 16px;
  position: relative;
  overflow: hidden;
}
.stat-card__icon {
  width: 38px; height: 38px;
  border-radius: 10px;
  background: rgba(255,255,255,0.25);
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 10px;
}
.stat-card__value { font-size: 26px; font-weight: 800; color: white; line-height: 1; }
.stat-card__label { font-size: 12px; color: rgba(255,255,255,0.8); margin-top: 4px; }
.stat-card--blue   { background: linear-gradient(135deg, #1565c0, #1976d2); }
.stat-card--red    { background: linear-gradient(135deg, #b71c1c, #e53935); }
.stat-card--green  { background: linear-gradient(135deg, #1b5e20, #43a047); }
.stat-card--orange { background: linear-gradient(135deg, #e65100, #fb8c00); }

.section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555;
  display: flex;
  align-items: center;
}

.status-breakdown-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 13px;
  color: white;
}
</style>
